import styled from "styled-components";
import { contentRevealMotion } from "../../../design-system/tokens/contentReveal";
import { shimmerFill } from "../../../design-system/tokens/shimmer";
import { typographyMixin } from "../../../design-system/tokens/typography";

export const Panel = styled.aside`
  display: flex;
  width: 100%;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  border: ${({ theme }) => theme.borders.width[1]}
    ${({ theme }) => theme.borders.style.solid}
    ${({ theme }) => theme.colors.border.stroke};
  border-radius: ${({ theme }) => theme.borders.radius.xl};
  background: ${({ theme }) => theme.colors.background.primary};
`;

export const Header = styled.header`
  display: flex;
  flex: 0 0 calc(
    ${({ theme }) => theme.spacing.base[11]} +
      ${({ theme }) => theme.spacing.base[0.5]}
  );
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.base[2]}
    ${({ theme }) => theme.spacing.base[3]};
  box-sizing: border-box;
  border-bottom: ${({ theme }) => theme.borders.width[1]}
    ${({ theme }) => theme.borders.style.solid}
    ${({ theme }) => theme.colors.border.stroke};
`;

export const Heading = styled.h2`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.base[2]};
  margin: 0;
  ${typographyMixin("subtitle", 2)}
  line-height: ${({ theme }) => theme.typography.heading.h3.lineHeight};
  color: ${({ theme }) => theme.colors.text.primary};

  svg {
    width: ${({ theme }) => theme.spacing.base[6]};
    height: ${({ theme }) => theme.spacing.base[6]};
    color: ${({ theme }) => theme.colors.text.interactive};
  }
`;

export const CloseButton = styled.button`
  display: inline-flex;
  width: ${({ theme }) => theme.spacing.base[6]};
  height: ${({ theme }) => theme.spacing.base[6]};
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: ${({ theme }) => theme.borders.radius.md};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;

  svg {
    width: ${({ theme }) => theme.spacing.base[5]};
    height: ${({ theme }) => theme.spacing.base[5]};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
  }

  &:focus-visible {
    outline: ${({ theme }) => theme.borders.width[2]}
      ${({ theme }) => theme.borders.style.solid}
      ${({ theme }) => theme.colors.primary[500]};
  }
`;

export const Recommendations = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.base[4]};
  padding: ${({ theme }) => theme.spacing.base[3]};
  overflow-y: auto;
  scrollbar-width: thin;
`;

export const RecommendationCard = styled.article<{ $previewed: boolean }>`
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.base[3]};
  padding: ${({ theme }) => theme.spacing.base[2]}
    ${({ theme }) => theme.spacing.base[3]}
    ${({ theme }) => theme.spacing.base[3]};
  box-sizing: border-box;
  border: ${({ theme }) => theme.borders.width[1]}
    ${({ theme }) => theme.borders.style.solid}
    ${({ $previewed, theme }) =>
      $previewed ? theme.colors.primary[300] : theme.colors.border.stroke};
  border-radius: ${({ theme }) => theme.borders.radius.xl};
  background: ${({ theme }) => theme.colors.background.tertiary};
  box-shadow: ${({ $previewed, theme }) =>
    $previewed ? theme.shadows.interactive.focus : "none"};
`;

export const RecommendationCopy = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
`;

export const RecommendationTitle = styled.h3`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.base[1]};
  margin: 0;
  ${typographyMixin("body", 2)}
  color: ${({ theme }) => theme.colors.text.primary};

  svg {
    flex: 0 0 ${({ theme }) => theme.spacing.base[3]};
    width: ${({ theme }) => theme.spacing.base[3]};
    height: ${({ theme }) => theme.spacing.base[3]};
    color: ${({ theme }) => theme.colors.text.interactive};
  }
`;

export const RecommendationDescription = styled.p`
  margin: 0;
  ${typographyMixin("caption", 4)}
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const CardActions = styled.div`
  display: flex;
  min-height: ${({ theme }) => theme.spacing.base[6]};
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.base[3]};
`;

export const PreviewButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  height: calc(
    ${({ theme }) => theme.spacing.base[6]} -
      ${({ theme }) => theme.spacing.base[0.5]}
  );
  align-items: center;
  gap: ${({ theme }) => theme.spacing.base[1]};
  padding: ${({ theme }) => theme.spacing.base[0.5]}
    ${({ theme }) => theme.spacing.base[2]};
  border: ${({ theme }) => theme.borders.width[1]}
    ${({ theme }) => theme.borders.style.solid}
    ${({ $active, theme }) =>
      $active ? theme.colors.primary[300] : theme.colors.border.stroke};
  border-radius: ${({ theme }) => theme.borders.radius.full};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primary[50] : theme.colors.background.primary};
  ${typographyMixin("caption", 4)}
  color: ${({ $active, theme }) =>
    $active ? theme.colors.text.interactive : theme.colors.text.secondary};
  cursor: pointer;

  svg {
    width: ${({ theme }) => theme.spacing.base[3]};
    height: ${({ theme }) => theme.spacing.base[3]};
  }
`;

export const ApplyButton = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  ${typographyMixin("body", 2)}
  color: ${({ theme }) => theme.colors.text.interactive};
  cursor: pointer;

  &:disabled {
    color: ${({ theme }) => theme.colors.text.tertiary};
    cursor: default;
  }

  &:focus-visible {
    outline: ${({ theme }) => theme.borders.width[2]}
      ${({ theme }) => theme.borders.style.solid}
      ${({ theme }) => theme.colors.primary[500]};
    outline-offset: ${({ theme }) => theme.borders.width[1]};
  }
`;

export const ErrorMessage = styled.p`
  margin: 0;
  ${typographyMixin("caption", 3)}
  color: ${({ theme }) => theme.colors.error.text};
`;

export const WorkspaceInfo = styled.section`
  flex: 0 0 ${({ theme }) => theme.spacing.base[28]};
  padding: ${({ theme }) => theme.spacing.base[3]};
  box-sizing: border-box;
  border-top: ${({ theme }) => theme.borders.width[1]}
    ${({ theme }) => theme.borders.style.solid}
    ${({ theme }) => theme.colors.border.stroke};
`;

export const WorkspaceHeading = styled.h3`
  display: flex;
  align-items: center;
  gap: calc(
    ${({ theme }) => theme.spacing.base[1]} +
      ${({ theme }) => theme.spacing.base[0.5]}
  );
  margin: 0 0 ${({ theme }) => theme.spacing.base[3]};
  ${typographyMixin("body", 2)}
  color: ${({ theme }) => theme.colors.text.primary};

  svg {
    width: ${({ theme }) => theme.spacing.base[4]};
    height: ${({ theme }) => theme.spacing.base[4]};
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

export const InfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.base[3]};
  ${typographyMixin("body", 4)}
  color: ${({ theme }) => theme.colors.text.secondary};

  & + & {
    margin-top: ${({ theme }) => theme.spacing.base[1]};
  }

  strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: inherit;
  }
`;

export const CardMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.base[1]};
`;

export const StateMessage = styled.p`
  margin: 0;
  padding: ${({ theme }) => theme.spacing.base[2]};
  ${typographyMixin("caption", 4)}
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const SkeletonCard = styled.div`
  height: ${({ theme }) => theme.spacing.base[16]};
  border-radius: ${({ theme }) => theme.borders.radius.xl};
  ${shimmerFill}
`;

/** Results taking over from the skeleton use the shared reveal motion. */
export const RevealList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.base[4]};
  ${contentRevealMotion}
`;
